import { useCallback, useState } from 'react';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api, ErroHttp } from '@/lib/api';
import type {
  MovimentacaoPedido,
  MovimentacaoResposta,
  NegativosResposta,
  OrigensResposta,
} from '@/lib/tipos';

export function useNegativos(centro: number | null): UseQueryResult<NegativosResposta, unknown> {
  return useQuery<NegativosResposta>({
    queryKey: ['negativos', centro],
    queryFn: () => api.get<NegativosResposta>('/api/negativos', { centro }),
    enabled: centro !== null,
    staleTime: 60_000,
    retry: (tentativas, erro) =>
      !(erro instanceof ErroHttp && (erro.naoAutenticado || erro.semPermissao)) && tentativas < 2,
  });
}

export function useOrigens(
  centro: number | null,
  produto: number | null,
): UseQueryResult<OrigensResposta, unknown> {
  return useQuery<OrigensResposta>({
    queryKey: ['negativos', 'origens', centro, produto],
    queryFn: () => api.get<OrigensResposta>(`/api/negativos/${produto}/origens`, { centro }),
    enabled: centro !== null && produto !== null,
    staleTime: 30_000,
  });
}

export type EstadoPerna = 'pendente' | 'executando' | 'ok' | 'erro' | 'cancelada';

export interface Perna {
  origem: number;
  origemEndereco: string;
  quantidade: number;
  estado: EstadoPerna;
  mensagem?: string | undefined;
}

interface RetornoCompensacao {
  pernas: Perna[];
  executando: boolean;
  /** `true` quando alguma perna já gravou — o negativo mudou no banco. */
  gravouAlgo: boolean;
  escritaDesabilitada: boolean;
  erroGeral: string | null;
  executar: (centro: number, produto: number, destino: number, pernas: Perna[]) => Promise<void>;
  reiniciar: () => void;
}

/**
 * Compensação de saldo negativo a partir de VÁRIAS origens.
 *
 * O pior caso do CD 2201 precisa de 260 un. que só existem espalhadas em 7
 * posições de 40, então uma chamada não resolve. Cada perna é um POST
 * independente e uma transação própria no PBS: se a terceira falhar, as duas
 * primeiras JÁ GRAVARAM. Por isso o estado é por perna e fica visível — a
 * tela não pode dar a impressão de "tudo ou nada" que o banco não oferece.
 */
export function useCompensacao(): RetornoCompensacao {
  const cliente = useQueryClient();
  const [pernas, definirPernas] = useState<Perna[]>([]);
  const [executando, definirExecutando] = useState(false);
  const [escritaDesabilitada, definirEscritaDesabilitada] = useState(false);
  const [erroGeral, definirErroGeral] = useState<string | null>(null);

  const reiniciar = useCallback(() => {
    definirPernas([]);
    definirExecutando(false);
    definirEscritaDesabilitada(false);
    definirErroGeral(null);
  }, []);

  const executar = useCallback(
    async (centro: number, produto: number, destino: number, iniciais: Perna[]) => {
      definirExecutando(true);
      definirErroGeral(null);
      definirEscritaDesabilitada(false);

      let atuais = iniciais.map((perna) => ({ ...perna, estado: 'pendente' as EstadoPerna }));
      definirPernas(atuais);

      const atualizar = (indice: number, mudanca: Partial<Perna>): void => {
        atuais = atuais.map((perna, i) => (i === indice ? { ...perna, ...mudanca } : perna));
        definirPernas(atuais);
      };

      for (let i = 0; i < atuais.length; i += 1) {
        const perna = atuais[i];
        if (!perna) continue;
        atualizar(i, { estado: 'executando' });
        try {
          const pedido: MovimentacaoPedido = {
            centro,
            produto,
            quantidade: perna.quantidade,
            origem: perna.origem,
            destino,
          };
          const resposta = await api.post<MovimentacaoResposta>('/api/movimentacao', pedido);
          atualizar(i, { estado: 'ok', mensagem: resposta.mensagem ?? 'Movimentação confirmada.' });
        } catch (erro) {
          const http = erro instanceof ErroHttp ? erro : null;
          if (http?.codigo === 'escrita_desabilitada') definirEscritaDesabilitada(true);
          atualizar(i, {
            estado: 'erro',
            mensagem: http?.message ?? 'Falha ao movimentar.',
          });
          // Para na primeira falha: insistir nas próximas origens depois de o
          // PBS recusar uma tende a multiplicar o problema, não a resolvê-lo.
          for (let j = i + 1; j < atuais.length; j += 1) atualizar(j, { estado: 'cancelada' });
          definirErroGeral(http?.message ?? 'Falha ao movimentar.');
          break;
        }
      }

      definirExecutando(false);
      await cliente.invalidateQueries({ queryKey: ['negativos'] });
      await cliente.invalidateQueries({ queryKey: ['mapa'] });
      await cliente.invalidateQueries({ queryKey: ['dashboard'] });
    },
    [cliente],
  );

  return {
    pernas,
    executando,
    gravouAlgo: pernas.some((perna) => perna.estado === 'ok'),
    escritaDesabilitada,
    erroGeral,
    executar,
    reiniciar,
  };
}
